import { useTranslation } from "react-i18next"
import ReactSelect from "react-select"
import { useCallback, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Button, Spinner } from "@inkblot-therapy/squid"
import { Referral } from "../../types/Referral"
import { formatReferralDate } from "../../utils/formatReferralDate"
import { StatusBadge } from "../StatusBadge"
import { AdminMeta } from "../../types/AdminMeta"
import { DataBaseService } from "../../services/DatabaseService"
import { useToastDispatch } from "../../context/ToastContextProvider"
import { Toast } from "../../types/ToastState"
import classNames from "classnames"

interface Props {
  referral: Referral
  edit?: boolean
  onCancel: () => unknown
  onSave: () => unknown
  onUpdateStatus: (nextStatus: Referral["status"]) => unknown
  adminMeta?: AdminMeta
  useReferralVersion2?: boolean
}

const referralStatusOptions: Array<{
  value: Referral["status"]
  label: JSX.Element
}> = [
  {
    value: "New",
    label: <StatusBadge status="New" />,
  },
  {
    value: "Review in Progress",
    label: <StatusBadge status="Review in Progress" />,
  },
  {
    value: "Incomplete",
    label: <StatusBadge status="Incomplete" />,
  },
  {
    value: "Complete",
    label: <StatusBadge status="Complete" />,
  },
  {
    value: "Fraud",
    label: <StatusBadge status="Fraud" />,
  },
  {
    value: "Rejected",
    label: <StatusBadge status="Rejected" />,
  },
]

export function ReferralReviewSidebar({
  referral,
  useReferralVersion2,
  edit,
  onCancel,
  onSave,
  onUpdateStatus,
  adminMeta,
}: Props) {
  const { referralId } = useParams()
  const [exporting, setExporting] = useState(false)
  const [exported, setExported] = useState(referral.exported_at !== null)
  const dispatch = useToastDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [referralStatus, setReferralStatus] = useState<Referral["status"]>(
    referral?.status ?? "New",
  )

  const referralStatusFieldValue = referralStatusOptions.find(
    (option) => option.value === referralStatus,
  )

  const handleReferralStatusChange = useCallback(
    (option: typeof referralStatusOptions[number] | null) => {
      if (option?.value) {
        setReferralStatus(option?.value)
        onUpdateStatus(option?.value)
      }
    },
    [onUpdateStatus],
  )

  const exportToTranquility = async () => {
    if (!referralId || referral.exported_at !== null || exported) return null

    try {
      setExporting(true)
      await DataBaseService.exportToTranquility(referralId, useReferralVersion2)
      dispatch({
        type: "ADD_TOAST",
        toast: {
          status: "success",
          header: t("admin.labels.successSaveHeader"),
          text: t("admin.labels.successSaveBody"),
        } as Toast,
      })
      setExported(true)
    } catch (err) {
      dispatch({
        type: "ADD_TOAST",
        toast: {
          status: "attention",
          header: t("admin.labels.errorSaveHeader"),
          text: t("admin.labels.errorSaveBody"),
        } as Toast,
      })
      console.error(err)
    }

    setExporting(false)

    return null
  }

  function getExportButtonText() {
    if (exporting) {
      return t("admin.labels.exporting")
    } else if (exported) {
      return t("admin.labels.exported")
    }
    return t("admin.labels.export")
  }

  return (
    <div className="w-full max-w-[360px] min-w-[300px] rounded-lg border border-[color:var(--alias-color-border-base)] p-4 mr-16">
      <p className="text-[color:var(--alias-color-text-info)] text-[length:var(--alias-font-overline-font-size)]">
        {referral.client_id}
      </p>
      <p className="mt-2 mb-6 text-[length:var(--alias-font-display-md-font-size)]">
        <strong>{adminMeta?.client}</strong>
      </p>
      <div className="mb-5">
        <p className="text-[color:var(--alias-color-text-weaker)] text-[length:var(--alias-font-label-strong-font-size)] mb-2">
          <strong>{t("admin.labels.referralDate")}</strong>
        </p>
        {adminMeta?.referral_date ? (
          <p>{formatReferralDate(new Date(adminMeta?.referral_date))}</p>
        ) : (
          "-"
        )}
      </div>
      <div className="mb-5">
        <p className="text-[color:var(--alias-color-text-weaker)] text-[length:var(--alias-font-label-strong-font-size)] mb-2">
          <strong>{t("admin.labels.referredBy")}</strong>
        </p>
        <p>
          {referral?.created_by ??
            referral?.nlo_contact_email ??
            referral?.referrer_email ??
            "-"}
        </p>
      </div>
      <div className="mb-5">
        <p className="text-[color:var(--alias-color-text-weaker)] text-[length:var(--alias-font-label-strong-font-size)] mb-2">
          <strong>{t("admin.labels.referralType")}</strong>
        </p>
        <p>{referral?.referral_submission_type ?? "Online"}</p>
      </div>
      <hr />
      <div className="mb-5 mt-6">
        <label
          htmlFor="referralStatus"
          className="block text-[color:var(--alias-color-text-weaker)] text-[length:var(--alias-font-label-strong-font-size)] mb-2"
        >
          <strong>{t("admin.labels.referralStatus")}</strong>
        </label>
        <ReactSelect
          isDisabled={referral.exported_at !== null || exported}
          // these are necessary in order to override react-select styles via classNames
          className="react-select"
          classNamePrefix="react-select"
          options={referralStatusOptions}
          // this is the actual id that gets matched with our label component
          inputId="referralStatus"
          onChange={handleReferralStatusChange}
          placeholder={`${t("admin.inputs.select")}...`}
          value={referralStatusFieldValue}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={false}
          isSearchable={false}
          styles={{
            option: (provided, _state) => ({
              ...provided,
              color: "var(--alias-color-text-base)",
            }),
          }}
        />
      </div>
      {!useReferralVersion2 && (
        <div className="flex items-center justify-between">
          {edit ? (
            <>
              <div className="mr-8">
                <Button variant="secondary" onClick={onCancel}>
                  {t("admin.buttons.cancel")}
                </Button>
              </div>
              <Button variant="primary" onClick={onSave}>
                {t("admin.buttons.saveChanges")}
              </Button>
            </>
          ) : (
            <Button
              className="w-full flex items-center justify-center mb-6"
              variant="primary"
              disabled={exported}
              onClick={() => {
                navigate("edit")
              }}
            >
              {t("admin.buttons.edit")}
            </Button>
          )}
        </div>
      )}

      <Button
        disabled={referralStatusFieldValue?.value !== "Complete"}
        variant="secondary"
        className={classNames("w-full flex items-center justify-center mb-2", {
          "mt-2": edit,
        })}
        onClick={() => exportToTranquility()}
      >
        {exporting && <Spinner />}
        {getExportButtonText()}
      </Button>
      {referral.exported_at !== null ||
        (exported && !useReferralVersion2 && (
          <p>{t("admin.labels.lockedAfterExport")}</p>
        ))}
    </div>
  )
}
